import React, { useEffect, useMemo, useState } from 'react';
import {
  useGetTSPsQuery,
  useGetZonesQuery,
  useListIOCContractsQuery,
} from '../services/edgeApi';
import { BelowMapSetter } from '../layouts/BelowMapContext';
import { BelowMapTab } from '../layouts/BelowMapTab';
import { useDispatch, useSelector } from 'react-redux';
import {
  iocActions,
  IOCContractsFilter,
  selectFilteredIOCContracts,
  selectIOCFilters,
} from './iocSlice';
import { MultiSelectSearch } from '../helpers/MultiSelect';
import { FormGroup, Label, Input } from 'reactstrap';
import { DateSelector } from '../helpers/DateSelector';
import { toIsoDate } from '../utils/stringUtils';
import { useTspFilter } from '../helpers/hooks';
import { ReusableModal } from '../modals/ReusableModal';
import { IOCTable } from './IOCTable';
import { selectTspOptions } from '../map/slice/mapSelectors';

export const IOC = () => {
  const dispatch = useDispatch();
  const setTspFilter = useTspFilter();

  const { data: tsps } = useGetTSPsQuery(undefined);
  const { data: zones } = useGetZonesQuery(undefined);

  const filters = useSelector(selectIOCFilters);
  const filteredIocContracts = useSelector(selectFilteredIOCContracts);

  const [filterTsps, setFilterTsps] = useState(true);
  const [gasDate, setGasDate] = useState(new Date());
  const [rawData, setRawData] = useState(new Object());
  const {
    data: iocContracts,
    isLoading,
    isFetching,
  } = useListIOCContractsQuery(toIsoDate(gasDate));

  const setFilter = (
    key: keyof IOCContractsFilter,
    value: string | string[],
  ) => {
    dispatch(iocActions.setFilter({ key, value }));
  };

  const sortedTsps = useMemo(
    () =>
      Object.values(tsps ?? {}).sort((a, b) => a.name.localeCompare(b.name)),
    [tsps],
  );

  const tspOptions = useSelector(selectTspOptions);

  useEffect(() => {
    return () => {
      dispatch(iocActions.resetState());
    };
  }, []);

  useEffect(() => {
    dispatch(iocActions.setContracts(iocContracts ?? []));
  }, [iocContracts]);

  useEffect(() => {
    if (filterTsps) {
      const highlightedTsps = new Set(
        filteredIocContracts.map((contract) => contract.tsp.id),
      );
      setTspFilter(
        highlightedTsps.size === sortedTsps.length
          ? []
          : Array.from(highlightedTsps),
      );
    }
  }, [filteredIocContracts, sortedTsps, filterTsps]);

  const uniqueShippers = useMemo(
    () =>
      [
        ...new Set(
          iocContracts?.map((contract) =>
            contract.owner
              ?.toUpperCase()
              .replace(/[^A-Z0-9 ]/g, '')
              .replace(/ +/g, ' '),
          ) ?? [],
        ),
      ].sort(),
    [iocContracts],
  );

  const uniqueAgentNames = useMemo(
    () =>
      [
        ...new Set(
          iocContracts?.map((contract) =>
            contract.agent_name
              ?.toUpperCase()
              .replace(/[^A-Z0-9 ]/g, '')
              .replace(/ +/g, ' '),
          ) ?? [],
        ),
      ].sort(),
    [iocContracts],
  );

  const uniqueRateSchedules = useMemo(
    () =>
      [
        ...new Set(
          iocContracts?.map((contract) => contract.raw_rate_schedule) ?? [],
        ),
      ].sort(),
    [iocContracts],
  );

  const filteredZones = useMemo(
    () =>
      Object.values(zones ?? {})
        .filter((zone) => zone.tsp && filters.tsp.includes(zone.tsp.id))
        .map((zone) => ({
          name: `${zone.tsp?.short_name || zone.tsp?.long_name}: ${zone.name}`,
          id: zone.id,
        })),
    [zones, filters.tsp],
  );

  const hideRawData = () => {
    setRawData(new Object());
  };

  return (
    <>
      <div className="d-flex flex-column gap-3">
        <h2>Index of Customers</h2>
        <DateSelector
          value={toIsoDate(gasDate)}
          onChange={(evt) => {
            if (evt.target.valueAsDate) {
              setGasDate(evt.target.valueAsDate);
            }
          }}
          label="Gas Date"
        />
        <h3>Filters</h3>
        <div>
          <div className="form-group">
            <div className="form-check form-switch">
              <input
                className="form-check-input"
                type="checkbox"
                id="filter-tsp-checkbox"
                checked={filterTsps}
                onChange={() => setFilterTsps(!filterTsps)}
              />
              <Label className="form-check-label">
                Show highlighted TSPs Only
              </Label>
            </div>
          </div>
          <FormGroup>
            <Label>Contract Number</Label>
            <Input
              type="text"
              onChange={(e) => setFilter('k_id', e.target.value)}
              value={filters.k_id}
            />
          </FormGroup>
          <FormGroup>
            <Label>Owner</Label>
            <MultiSelectSearch
              onChange={(value) =>
                setFilter(
                  'owner',
                  value.map((v) => v.value),
                )
              }
              value={filters.owner}
              options={uniqueShippers.map((shipper) => ({
                value: shipper,
                label: shipper,
              }))}
            />
          </FormGroup>
          <FormGroup>
            <Label>Agent Name</Label>
            <MultiSelectSearch
              onChange={(value) =>
                setFilter(
                  'agent_name',
                  value.map((v) => v.value),
                )
              }
              value={filters.agent_name}
              options={uniqueAgentNames.map((agentName) => ({
                value: agentName,
                label: agentName,
              }))}
            />
          </FormGroup>
          <div className="row">
            <div className="col-6">
              <FormGroup>
                <Label>TSP</Label>
                <MultiSelectSearch
                  onChange={(value) =>
                    setFilter(
                      'tsp',
                      value.map((v) => v.value as unknown as string),
                    )
                  }
                  value={filters.tsp}
                  options={tspOptions}
                />
              </FormGroup>
            </div>
            <div className="col-6">
              <FormGroup>
                <Label>Rate Schedule</Label>
                <MultiSelectSearch
                  onChange={(value) =>
                    setFilter(
                      'raw_rate_schedule',
                      value.map((v) => v.value),
                    )
                  }
                  value={filters.raw_rate_schedule}
                  options={uniqueRateSchedules.map((rateSchedule) => ({
                    value: rateSchedule,
                    label: rateSchedule,
                  }))}
                />
              </FormGroup>
            </div>
          </div>
          <div className="row">
            <div className="col-6">
              <FormGroup>
                <Label>Receipt Zone</Label>
                <MultiSelectSearch
                  onChange={(value) =>
                    setFilter(
                      'receipt_zones',
                      value.map((v) => v.value),
                    )
                  }
                  value={filters.receipt_zones}
                  options={filteredZones.map((zone) => ({
                    value: zone.id,
                    label: zone.name,
                  }))}
                />
              </FormGroup>
            </div>
            <div className="col-6">
              <FormGroup>
                <Label>Delivery Zone</Label>
                <MultiSelectSearch
                  onChange={(value) =>
                    setFilter(
                      'delivery_zones',
                      value.map((v) => v.value),
                    )
                  }
                  value={filters.delivery_zones}
                  options={filteredZones.map((zone) => ({
                    value: zone.id,
                    label: zone.name,
                  }))}
                />
              </FormGroup>
            </div>
          </div>
          <div className="row">
            <div className="col-6">
              <FormGroup>
                <Label>Receipt Point</Label>
                <Input
                  type="text"
                  onChange={(e) => setFilter('receipt_points', e.target.value)}
                  value={filters.receipt_points}
                />
              </FormGroup>
            </div>
            <div className="col-6">
              <FormGroup>
                <Label>Delivery Point</Label>
                <Input
                  type="text"
                  onChange={(e) => setFilter('delivery_points', e.target.value)}
                  value={filters.delivery_points}
                />
              </FormGroup>
            </div>
          </div>
        </div>
      </div>
      <BelowMapSetter>
        <BelowMapTab tabName="Index of Customers" contextKey="ioc-tab">
          <IOCTable
            iocContracts={filteredIocContracts}
            gasDate={gasDate}
            onShowRawData={setRawData}
            isLoading={isLoading || isFetching}
          />
        </BelowMapTab>
      </BelowMapSetter>
      <ReusableModal
        size={'lg'}
        isOpen={Object.keys(rawData).length > 0}
        header={<span className="fs-3">Raw Data</span>}
        onClose={hideRawData}
      >
        <pre>
          <code>{JSON.stringify(rawData, null, 2)}</code>
        </pre>
      </ReusableModal>
    </>
  );
};
